<script setup lang="ts">
import ToastContainer from "./components/ToastContainer.vue";
import { onMounted, onUnmounted, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useSessionStore } from "./stores/session";
import { useTheme } from "./composables/useTheme";

const router = useRouter();
const session = useSessionStore();

// Initialize theme system globally
const { initializeTheme } = useTheme();
let cleanupTheme: (() => void) | null = null;

// Get restaurant accent color for loading spinner with localStorage caching
const loadingAccentColor = computed(() => {
  const restaurant = session.currentRestaurant;
  const restaurantColor = restaurant?.theme?.accent || restaurant?.primaryColor;

  // If we have restaurant color, cache it and use it
  if (restaurantColor) {
    localStorage.setItem("qrmb-restaurant-accent", restaurantColor);
    return restaurantColor;
  }

  // If no restaurant loaded yet, try to get cached color
  const cachedColor = localStorage.getItem("qrmb-restaurant-accent");
  return cachedColor || "#f97316"; // fallback to orange
});

// Watch for restaurant changes (venue switch or appearance update) and update cache
watch(
  () => session.currentRestaurant,
  (restaurant) => {
    if (restaurant) {
      const accentColor = restaurant.theme?.accent || restaurant.primaryColor;
      if (accentColor) {
        localStorage.setItem("qrmb-restaurant-accent", accentColor);
      }
    }
  },
  { deep: true }
);

// Determine when app is fully ready to show
const isAppReady = computed(() => {
  const currentPath = router.currentRoute.value.path;
  const currentName = router.currentRoute.value.name;

  // Show loading during logout transition (even on public routes like landing)
  // This prevents flash of sidebar disappearing during logout
  if (session.isTransitioning) return false;

  // For public routes (landing, menu, legal pages), ALWAYS show immediately
  // This is critical for SEO - Google must be able to render these pages
  const publicRoutes = ["/", "/terms", "/privacy"];
  const publicPaths = ["/m/", "/r/", "/invite/", "/l/"];

  if (
    publicRoutes.includes(currentPath) ||
    publicPaths.some((route) => currentPath.startsWith(route)) ||
    currentName === "landing"
  ) {
    return true;
  }

  // Show loading if user is null but auth thinks we're authenticated
  if (session.isAuthenticated && !session.user) return false;

  // Auth must be initialized first
  if (!session.isInitialized) return false;

  // If we're on the auth page and user is authenticated AND verified, keep loading screen visible
  // while the redirect happens (prevents flash of auth page)
  // But allow unverified users to see the verification screen
  if (currentPath === "/auth" && session.isAuthenticated && session.user?.emailVerified) return false;

  // For authenticated users, always wait for restaurant data to prevent flashes
  if (session.isAuthenticated) {
    return session.isRestaurantLoaded;
  }

  // For unauthenticated users, show immediately
  return true;
});

// Global cookie management functions
onMounted(() => {
  // Initialize theme system first
  cleanupTheme = initializeTheme();

  // Global function to reset cookie consent
  (window as any).resetCookieConsent = () => {
    localStorage.removeItem("cookie-consent");
    router.push("/");
  };

  // Initialize session
  session.initialize();
});

onUnmounted(() => {
  session.cleanup();
  if (cleanupTheme) {
    cleanupTheme();
  }
});
</script>

<template>
  <div class="min-h-screen bg-primary text-primary relative">
    <!-- Single Global Loading Screen - Fixed overlay -->
    <div v-if="!isAppReady" class="fixed inset-0 z-[9999] bg-primary flex items-center justify-center">
      <div class="text-center">
        <div
          class="w-12 h-12 border-4 rounded-full animate-spin mx-auto mb-4"
          :style="{
            borderColor: `${loadingAccentColor}20`,
            borderTopColor: loadingAccentColor,
          }"
        ></div>
        <p class="text-secondary text-lg">Loading...</p>
      </div>
    </div>

    <!-- App Content - Always rendered but covered by loading screen when needed -->
    <RouterView />
    <ToastContainer />
  </div>
</template>
